﻿package org.example.project.engineletter

import androidx.compose.runtime.Composable
import org.example.project.settings.AppDropdown
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.bottom
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.right
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private object LetterButtonBarStyles : StyleSheet() {
    val barRoot by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(18.px)
        width(100.percent)
        padding(8.px, 0.px, 18.px, 0.px)
        property("flex-wrap", "wrap")
    }

    val cluster by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
    }

    val iconButton by style {
        property("appearance", "none")
        property("-webkit-appearance", "none")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        width(40.px)
        height(40.px)
        backgroundColor(Color("#1c2430"))
        borderRadius(10.px)
        border(1.px, LineStyle.Solid, Color("#334154"))
        cursor("pointer")
        padding(0.px)
        hover {
            backgroundColor(Color("#253244"))
        }
    }

    val iconButtonDisabled by style {
        backgroundColor(Color("#131923"))
        color(Color("rgba(255,255,255,0.36)"))
        cursor("default")
    }

    val icon by style {
        width(18.px)
        height(18.px)
        property("object-fit", "contain")
    }

    val popupOverlay by style {
        position(Position.Fixed)
        top(0.px)
        left(0.px)
        right(0.px)
        bottom(0.px)
        backgroundColor(Color("rgba(8,12,18,0.55)"))
        property("z-index", "4000")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }

    val popupCard by style {
        width(640.px)
        backgroundColor(Color("#15202b"))
        borderRadius(16.px)
        border(1.px, LineStyle.Solid, Color("#334154"))
        padding(22.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        color(Color.white)
        property("box-shadow", "0 28px 80px rgba(0,0,0,0.40)")
    }

    val popupTitle by style {
        fontSize(18.px)
        property("font-weight", "700")
    }

    val popupGrid by style {
        display(DisplayStyle.Flex)
        property("flex-wrap", "wrap")
        gap(14.px)
    }

    val popupField by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
    }

    val popupLabel by style {
        fontSize(12.px)
        color(Color("#c9d4e3"))
        textAlign("left")
    }

    val popupActions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        property("flex-wrap", "wrap")
    }

    val popupActionButton by style {
        property("appearance", "none")
        property("-webkit-appearance", "none")
        backgroundColor(Color("#223247"))
        color(Color.white)
        borderRadius(10.px)
        border(1.px, LineStyle.Solid, Color("#38506e"))
        padding(10.px, 14.px)
        cursor("pointer")
    }
}

@Composable
fun LetterButtonBar(
    layoutOptionen: List<All>,
    selectedLayoutKey: String,
    empfaengerOptionen: List<All>,
    selectedEmpfaengerKey: String,
    isSaving: Boolean,
    documentActive: Boolean,
    onNeu: () -> Unit,
    onLayoutChange: (String) -> Unit,
    onEmpfaengerChange: (String) -> Unit,
    onLogoImport: () -> Unit,
    onSave: () -> Unit,
    onUnterzeichnen: (() -> Unit)? = null
) {
    Style(cssRules = LetterButtonBarStyles.cssRules)
    Div({ classes(LetterButtonBarStyles.barRoot) }) {
        if (documentActive) {
            Div({ classes(LetterButtonBarStyles.cluster) }) {
                LetterIconButton(iconSrc = "images/view_apps.svg", title = "Layoutauswahl", enabled = true, onClick = onNeu)
                LetterIconButton(iconSrc = "images/save.svg", title = "Speichern", enabled = !isSaving, onClick = onSave)
                LetterIconButton(iconSrc = "images/add_logo.svg", title = "Logo importieren", enabled = true, onClick = onLogoImport)
                onUnterzeichnen?.let { LetterPopupAction(label = "Unterzeichnen", onClick = it) }
            }
            Div({ classes(LetterButtonBarStyles.cluster); style { property("flex-wrap", "wrap") } }) {
                LetterBarDropdown("Empfänger", 210, empfaengerOptionen, selectedEmpfaengerKey, onEmpfaengerChange)
            }
        } else {
            Span({ style { width(100.percent); color(Color.white); fontSize(18.px); property("font-weight", "700"); textAlign("center") } }) { Text("Layout auswählen") }
        }
    }

}

@Composable
private fun LetterSettingsPopup(
    layoutOptionen: List<All>,
    selectedLayoutKey: String,
    onClose: () -> Unit,
    onLayoutChange: (String) -> Unit,
    onEmpfaengerChange: (String) -> Unit,
    onHeaderModeChange: (String) -> Unit,
    onFooterModeChange: (String) -> Unit,
    onLogoImport: () -> Unit
) {
    Div({
        classes(LetterButtonBarStyles.popupOverlay)
        onClick { onClose() }
    }) {
        Div({
            classes(LetterButtonBarStyles.popupCard)
            onClick { it.stopPropagation() }
        }) {
            Span({ classes(LetterButtonBarStyles.popupTitle) }) { Text("Dokument-Einstellungen") }
            Div({ classes(LetterButtonBarStyles.popupActions) }) {
                LetterPopupAction(label = "Logo importieren", onClick = onLogoImport)
                LetterPopupAction(label = "Schließen", onClick = onClose)
            }
        }
    }
}

@Composable
private fun LetterBarDropdown(label: String, widthPx: Int, options: List<All>, selected: String, onChange: (String) -> Unit) {
    Div({ classes(LetterButtonBarStyles.popupField) }) {
        Span({ classes(LetterButtonBarStyles.popupLabel) }) { Text(label) }
        AppDropdown(options = options, selectedValue = selected, onChange = onChange, attrs = { style { width(widthPx.px); height(40.px) } })
    }
}

@Composable
private fun LetterPopupDropdown(
    label: String,
    widthPx: Int,
    options: List<All>,
    selectedValue: String,
    onChange: (String) -> Unit
) {
    Div({ classes(LetterButtonBarStyles.popupField) }) {
        Span({ classes(LetterButtonBarStyles.popupLabel) }) { Text(label) }
        AppDropdown(
            options = options,
            selectedValue = selectedValue,
            onChange = onChange,
            attrs = { style { width(widthPx.px); height(42.px) } }
        )
    }
}

@Composable
private fun LetterPopupAction(label: String, onClick: () -> Unit) {
    Button(attrs = {
        classes(LetterButtonBarStyles.popupActionButton)
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun LetterIconButton(
    iconSrc: String,
    title: String,
    enabled: Boolean,
    onClick: () -> Unit
) {
    Button(attrs = {
        classes(LetterButtonBarStyles.iconButton)
        attr("title", title)
        if (!enabled) classes(LetterButtonBarStyles.iconButtonDisabled)
        if (enabled) onClick { onClick() }
    }) {
        Img(src = iconSrc, attrs = { classes(LetterButtonBarStyles.icon) })
    }
}
